import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  History, Play, Pause, SkipBack, SkipForward,
  Clock, FileText, ChevronLeft, FastForward, Rewind,
  AlertTriangle, Zap, PenTool, ClipboardPaste, Activity, Timer,
  CheckCircle
} from 'lucide-react';
import { useAuth } from '../lib/AuthContext';
import { getDrafts, getTrackEvents } from '../lib/api';
import { DraftyMascot } from './DraftyMascot';
import { studentLabel } from '../lib/format';
import { Slider } from '../components/ui/slider';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine
} from 'recharts';
import { BRAND } from '../lib/brandColors';

export const DraftTrack = ({ draftId, onClose }) => {
  const { profile, isTeacher } = useAuth();
  const [drafts, setDrafts] = useState([]);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [currentContent, setCurrentContent] = useState('');
  
  const playbackRef = useRef(null);

  useEffect(() => {
    if (draftId) {
      loadTrackEvents(draftId);
    } else {
      loadDrafts();
    }
  }, [draftId, profile]);

  const loadDrafts = async () => {
    try {
      // Teachers see all drafts, students see their own
      const draftList = isTeacher 
        ? await getDrafts() 
        : await getDrafts(profile?.id);
      setDrafts(draftList || []);
    } catch (error) {
      console.error('Failed to load drafts:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTrackEvents = async (id) => {
    setLoading(true);
    try {
      const trackEvents = await getTrackEvents(id);
      setEvents(trackEvents || []);
      if (trackEvents && trackEvents.length > 0) {
        setCurrentContent('');
        setCurrentIndex(0);
      }
    } catch (error) {
      console.error('Failed to load track events:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectDraft = async (draft) => {
    setSelectedDraft(draft);
    await loadTrackEvents(draft.id);
  };

  // Playback controls
  useEffect(() => {
    if (isPlaying && events.length > 0) {
      const interval = 1000 / playbackSpeed; // Adjust speed
      
      playbackRef.current = setInterval(() => {
        setCurrentIndex(prev => {
          if (prev >= events.length - 1) {
            setIsPlaying(false);
            return prev;
          }
          return prev + 1;
        });
      }, interval);
    }
    
    return () => {
      if (playbackRef.current) {
        clearInterval(playbackRef.current);
      }
    };
  }, [isPlaying, events.length, playbackSpeed]);

  // Update content based on current index
  useEffect(() => {
    if (events.length > 0 && currentIndex < events.length) {
      // Reconstruct content up to current event
      let content = '';
      for (let i = 0; i <= currentIndex; i++) {
        const event = events[i];
        if (event.content_diff) {
          content = event.content_diff; // Each event stores full content snapshot
        }
      }
      setCurrentContent(content);
    }
  }, [currentIndex, events]);

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (value) => {
    setCurrentIndex(value[0]);
    setIsPlaying(false);
  };

  const handleSkipBack = () => {
    setCurrentIndex(Math.max(0, currentIndex - 10));
    setIsPlaying(false);
  };

  const handleSkipForward = () => {
    setCurrentIndex(Math.min(events.length - 1, currentIndex + 10));
    setIsPlaying(false);
  };

  const handleReset = () => {
    setCurrentIndex(0);
    setIsPlaying(false);
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  const getCurrentEvent = () => {
    return events[currentIndex] || null;
  };

  const computeInsights = () => {
    if (events.length < 2) return null;

    const firstTime = new Date(events[0].timestamp).getTime();
    const lastTime = new Date(events[events.length - 1].timestamp).getTime();
    const totalMs = lastTime - firstTime;
    const totalMinutes = totalMs / 60000;
    const finalWordCount = events[events.length - 1].word_count || 0;
    const avgWpm = totalMinutes > 0 ? (finalWordCount / totalMinutes).toFixed(1) : 0;

    let largestAddition = 0;
    let pasteAlerts = [];
    let sessions = 1;

    for (let i = 1; i < events.length; i++) {
      const delta = (events[i].word_count || 0) - (events[i - 1].word_count || 0);
      const timeDelta = (new Date(events[i].timestamp).getTime() - new Date(events[i - 1].timestamp).getTime()) / 1000;

      if (delta > largestAddition) largestAddition = delta;

      if (delta > 50 && timeDelta < 10) {
        pasteAlerts.push({ index: i, delta, timeDelta, severity: delta > 200 ? 'high' : 'medium' });
      }

      if (timeDelta > 1800) sessions++;
    }

    const formatDuration = (ms) => {
      const hours = Math.floor(ms / 3600000);
      const mins = Math.floor((ms % 3600000) / 60000);
      if (hours > 0) return `${hours}h ${mins}m`;
      return `${mins}m`;
    };

    return {
      totalTime: formatDuration(totalMs),
      avgWpm,
      totalRevisions: events.length,
      largestAddition,
      pasteAlerts,
      sessions,
    };
  };

  const getVelocityData = () => {
    return events.map((event, idx) => ({
      index: idx,
      time: new Date(event.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      words: event.word_count || 0,
      type: event.event_type,
    }));
  };

  const getEventLabel = (event, prevEvent) => {
    if (!prevEvent) return { label: 'Started Writing', color: 'bg-teal-flow/20 text-teal-flow' };

    const delta = (event.word_count || 0) - (prevEvent.word_count || 0);
    const timeDelta = (new Date(event.timestamp).getTime() - new Date(prevEvent.timestamp).getTime()) / 1000;

    if (delta > 200) return { label: `Bulk Addition (+${delta})`, color: 'bg-red-500/20 text-red-400' };
    if (delta > 100) return { label: `Large Addition (+${delta})`, color: 'bg-amber-500/20 text-amber-400' };
    if (delta > 50 && timeDelta < 10) return { label: `Possible Paste (+${delta})`, color: 'bg-red-500/20 text-red-400' };
    if (delta > 0) return { label: `Typing (+${delta} words)`, color: 'bg-green-500/20 text-green-400' };
    if (delta < -50) return { label: `Major Delete (${delta})`, color: 'bg-red-500/20 text-red-400' };
    if (delta < 0) return { label: `Editing (${delta} words)`, color: 'bg-slate-700 text-slate-400' };
    return { label: 'Minor Edit', color: 'bg-slate-700 text-slate-400' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-flow-blue border-t-transparent" />
      </div>
    );
  }

  // Draft selection view (for teachers or when no draftId provided)
  if (!selectedDraft && !draftId) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="font-heading text-2xl font-bold text-white flex items-center gap-3">
            <History className="text-ai-accent" size={28} />
            DraftTrack
          </h2>
          <p className="text-slate-400 mt-1">
            {isTeacher 
              ? "Watch a time-lapse of student writing to verify authenticity"
              : "Review your writing journey and see how your draft evolved"
            }
          </p>
        </div>

        {drafts.length === 0 ? (
          <div className="card-surface p-12 text-center">
            <DraftyMascot size="md" mood="thinking" className="mx-auto mb-4" />
            <p className="text-slate-400">No drafts with revision history yet.</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {drafts.map((draft) => (
              <motion.div
                key={draft.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="card-surface p-5 hover:border-ai-accent/30 transition-colors cursor-pointer"
                onClick={() => selectDraft(draft)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-ai-accent/20 rounded-lg">
                      <FileText className="text-ai-accent" size={24} />
                    </div>
                    <div>
                      <h3 className="text-white font-medium">
                        {isTeacher ? `${studentLabel(draft)}'s Draft` : 'Your Draft'}
                      </h3>
                      <p className="text-slate-400 text-sm">
                        {draft.word_count || 0} words • {draft.status}
                      </p>
                    </div>
                  </div>
                  <button className="btn-purple px-4 py-2 rounded-lg flex items-center gap-2">
                    <Play size={16} />
                    Watch Playback
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Playback view
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-700 rounded-lg text-slate-400"
            >
              <ChevronLeft size={20} />
            </button>
          )}
          {!draftId && (
            <button
              onClick={() => {
                setSelectedDraft(null);
                setEvents([]);
                setCurrentContent('');
              }}
              className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
            >
              <ChevronLeft size={20} />
              Back to Drafts
            </button>
          )}
          <div>
            <h2 className="font-heading text-xl font-semibold text-white flex items-center gap-2">
              <History className="text-ai-accent" size={24} />
              Revision Playback
            </h2>
            <p className="text-slate-400 text-sm">
              {events.length} revision events recorded
            </p>
          </div>
        </div>
      </div>

      {/* Integrity Insights Panel */}
      {events.length >= 2 && (() => {
        const insights = computeInsights();
        if (!insights) return null;
        return (
          <div className="card-surface p-5">
            <h3 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Activity className="text-teal-flow" size={20} />
              Writing Integrity Insights
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div className="bg-slate-900 rounded-lg p-3">
                <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
                  <Timer size={14} />
                  Total Writing Time
                </div>
                <p className="text-white font-semibold text-lg">{insights.totalTime}</p>
              </div>
              <div className="bg-slate-900 rounded-lg p-3">
                <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
                  <Zap size={14} />
                  Avg. Speed
                </div>
                <p className="text-white font-semibold text-lg">{insights.avgWpm} <span className="text-sm text-slate-400">wpm</span></p>
              </div>
              <div className="bg-slate-900 rounded-lg p-3">
                <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
                  <PenTool size={14} />
                  Revisions
                </div>
                <p className="text-white font-semibold text-lg">{insights.totalRevisions}</p>
              </div>
              <div className="bg-slate-900 rounded-lg p-3">
                <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
                  <FileText size={14} />
                  Largest Addition
                </div>
                <p className={`font-semibold text-lg ${insights.largestAddition > 100 ? 'text-amber-400' : 'text-white'}`}>
                  +{insights.largestAddition} <span className="text-sm text-slate-400">words</span>
                </p>
              </div>
              <div className={`rounded-lg p-3 ${insights.pasteAlerts.length > 0 ? 'bg-red-500/10 border border-red-500/20' : 'bg-slate-900'}`}>
                <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
                  <ClipboardPaste size={14} />
                  Paste Alerts
                </div>
                <p className={`font-semibold text-lg ${insights.pasteAlerts.length > 0 ? 'text-red-400' : 'text-green-400'}`}>
                  {insights.pasteAlerts.length > 0 ? (
                    <span className="flex items-center gap-1">
                      <AlertTriangle size={16} /> {insights.pasteAlerts.length}
                    </span>
                  ) : (
                    'None'
                  )}
                </p>
              </div>
              <div className="bg-slate-900 rounded-lg p-3">
                <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
                  <History size={14} />
                  Writing Sessions
                </div>
                <p className="text-white font-semibold text-lg">{insights.sessions}</p>
              </div>
            </div>

            {/* Paste Alert Details */}
            {insights.pasteAlerts.length > 0 && (
              <div className="mt-4 space-y-2">
                <p className="text-amber-400 text-sm font-medium flex items-center gap-1">
                  <AlertTriangle size={14} />
                  Suspicious Activity Detected
                </p>
                {insights.pasteAlerts.map((alert, idx) => (
                  <button
                    key={idx}
                    onClick={() => { setCurrentIndex(alert.index); setIsPlaying(false); }}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between transition-colors ${
                      alert.severity === 'high'
                        ? 'bg-red-500/10 border border-red-500/30 hover:bg-red-500/20 text-red-300'
                        : 'bg-amber-500/10 border border-amber-500/30 hover:bg-amber-500/20 text-amber-300'
                    }`}
                  >
                    <span>+{alert.delta} words added in {Math.round(alert.timeDelta)}s</span>
                    <span className="text-xs opacity-70">Click to view</span>
                  </button>
                ))}
              </div>
            )}
            {insights.pasteAlerts.length === 0 && insights.totalRevisions >= 5 && (
              <div className="mt-4 px-3 py-2 rounded-lg bg-green-500/10 border border-green-500/20">
                <p className="text-green-400 text-sm flex items-center gap-2">
                  <CheckCircle size={14} />
                  Writing pattern appears authentic — steady progression with no anomalies detected.
                </p>
              </div>
            )}
          </div>
        );
      })()}

      {events.length === 0 ? (
        <div className="card-surface p-12 text-center">
          <DraftyMascot size="md" mood="thinking" className="mx-auto mb-4" />
          <p className="text-slate-400">No revision history recorded for this draft yet.</p>
          <p className="text-slate-500 text-sm mt-2">
            Revision events are captured as the student writes.
          </p>
        </div>
      ) : (
        <>
          {/* Content Display */}
          <div className="card-surface p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-slate-400 text-sm">
                Event {currentIndex + 1} of {events.length}
              </span>
              {getCurrentEvent() && (
                <span className="text-slate-500 text-xs flex items-center gap-1">
                  <Clock size={12} />
                  {formatTimestamp(getCurrentEvent().timestamp)}
                </span>
              )}
            </div>
            
            <div className="bg-slate-900 rounded-lg p-6 min-h-[300px] max-h-[400px] overflow-y-auto" aria-live="polite">
              {currentContent ? (
                <div
                  className="font-editor text-slate-300 leading-relaxed max-w-none [&_p]:mb-3 [&_strong]:text-white [&_strong]:font-bold [&_em]:text-slate-200 [&_em]:italic [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:mb-1"
                  dangerouslySetInnerHTML={{ __html: currentContent }}
                />
              ) : (
                <p className="font-editor text-slate-400 italic">
                  Start playback to see the writing process...
                </p>
              )}
            </div>

            {getCurrentEvent() && (() => {
              const label = getEventLabel(getCurrentEvent(), currentIndex > 0 ? events[currentIndex - 1] : null);
              return (
                <div className="mt-4 flex items-center gap-4 text-sm">
                  <span className={`status-chip ${
                    label.color.includes('red') ? 'status-chip--alert' :
                    label.color.includes('amber') ? 'status-chip--alert' :
                    label.color.includes('green') ? 'status-chip--success' :
                    label.color.includes('teal-flow') ? 'status-chip--primary' :
                    'status-chip--gold'
                  }`}>
                    {label.label}
                  </span>
                  <span className="text-slate-500">
                    {getCurrentEvent().word_count || 0} words at this point
                  </span>
                </div>
              );
            })()}
          </div>

          {/* Writing Velocity Chart */}
          <div className="card-surface p-5">
            <h3 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Activity className="text-teal-flow" size={20} />
              Writing Velocity
            </h3>
            <div className="h-[150px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={getVelocityData()}
                  onClick={(e) => {
                    if (e && e.activeTooltipIndex !== undefined) {
                      setCurrentIndex(e.activeTooltipIndex);
                      setIsPlaying(false);
                    }
                  }}
                  className="cursor-pointer"
                >
                  <defs>
                    <linearGradient id="tealGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={BRAND.tealFlow} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={BRAND.tealFlow} stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="time"
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    axisLine={{ stroke: '#334155' }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    axisLine={false}
                    tickLine={false}
                    width={40}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: BRAND.surfaceRaised,
                      border: '1px solid #334155',
                      borderRadius: '8px',
                      color: '#e2e8f0',
                      fontSize: '12px',
                    }}
                    formatter={(value) => [`${value} words`, 'Word Count']}
                  />
                  <ReferenceLine
                    x={getVelocityData()[currentIndex]?.time}
                    stroke={BRAND.aiAccent}
                    strokeDasharray="3 3"
                    strokeWidth={2}
                  />
                  <Area
                    type="monotone"
                    dataKey="words"
                    stroke={BRAND.tealFlow}
                    strokeWidth={2}
                    fill="url(#tealGradient)"
                    dot={false}
                    activeDot={{ r: 4, fill: BRAND.tealFlow, stroke: '#fff', strokeWidth: 2 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <p className="text-slate-500 text-xs mt-2 text-center">Click any point to jump to that revision</p>
          </div>

          {/* Playback Controls */}
          <div className="card-surface p-6">
            {/* Timeline Slider */}
            <div className="mb-6">
              <Slider
                value={[currentIndex]}
                max={Math.max(0, events.length - 1)}
                step={1}
                onValueChange={handleSeek}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-slate-500 mt-2">
                <span>Start</span>
                <span>End</span>
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={handleReset}
                className="p-2 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white transition-colors"
                title="Reset to beginning"
              >
                <SkipBack size={20} />
              </button>
              
              <button
                onClick={handleSkipBack}
                className="p-2 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white transition-colors"
                title="Skip back 10 events"
              >
                <Rewind size={20} />
              </button>

              <button
                onClick={handlePlayPause}
                aria-label={isPlaying ? "Pause revision playback" : "Play revision history"}
                className="p-4 bg-ai-accent hover:bg-ai-accent-hover rounded-full text-white transition-colors"
              >
                {isPlaying ? <Pause size={24} /> : <Play size={24} />}
              </button>

              <button
                onClick={handleSkipForward}
                className="p-2 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white transition-colors"
                title="Skip forward 10 events"
              >
                <FastForward size={20} />
              </button>

              {/* Speed Control */}
              <div className="flex items-center gap-2 ml-4 border-l border-slate-700 pl-4">
                <span className="text-slate-400 text-sm">Speed:</span>
                {[0.5, 1, 2, 4].map((speed) => (
                  <button
                    key={speed}
                    onClick={() => setPlaybackSpeed(speed)}
                    className={`px-2 py-1 rounded text-sm ${
                      playbackSpeed === speed
                        ? 'bg-flow-blue text-white'
                        : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                    }`}
                  >
                    {speed}x
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Event Timeline */}
          <div className="card-surface p-6">
            <h3 className="font-heading text-lg font-semibold text-white mb-4">
              Event Timeline
            </h3>
            <div className="max-h-[200px] overflow-y-auto space-y-2">
              {events.map((event, idx) => {
                const label = getEventLabel(event, idx > 0 ? events[idx - 1] : null);
                return (
                  <div
                    key={event.id || idx}
                    onClick={() => {
                      setCurrentIndex(idx);
                      setIsPlaying(false);
                    }}
                    className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                      idx === currentIndex
                        ? 'bg-ai-accent/20 border border-ai-accent/40'
                        : 'hover:bg-slate-800'
                    }`}
                  >
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                      label.color.includes('red') ? 'bg-red-500' :
                      label.color.includes('amber') ? 'bg-amber-500' :
                      label.color.includes('green') ? 'bg-green-500' :
                      label.color.includes('teal-flow') ? 'bg-teal-flow' :
                      'bg-slate-500'
                    }`} />
                    <span className="text-slate-400 text-xs font-mono">
                      {formatTimestamp(event.timestamp)}
                    </span>
                    <span className="text-slate-300 text-sm truncate">
                      {label.label}
                    </span>
                    <span className="text-slate-500 text-xs ml-auto flex-shrink-0">
                      {event.word_count} words
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DraftTrack;
